import nlp from "compromise"
import { store, mentionPattern, matchedEntity } from "../editor/store"

const KIND_ORDER = ["character", "location", "thread", "item", "faction", "other"]

function titleize(text) {
  return text.replace(/\b\p{L}/gu, (letter) => letter.toUpperCase())
}

function pluralize(noun) {
  const plural = nlp(noun).nouns().toPlural().out("text")
  return plural || `${noun}s`
}

export function groupLabel(headings) {
  if (!headings.length) return null
  const words = headings[0].title.match(/[\p{L}\p{N}']+/gu)
  if (!words) return null
  const nouns = nlp(words.join(" ")).nouns().json()
  const noun = nouns.length ? nouns[0].terms[0].text : words[0]
  return titleize(pluralize(noun))
}

export function analyzeDocument(text) {
  const lines = text.split("\n")
  const sections = []
  let current = { level: 0, title: null, from: 0, lines: [] }
  let offset = 0

  lines.forEach((line) => {
    const heading = line.match(/^(#{1,6})\s+(.+)$/)
    if (heading) {
      sections.push(current)
      current = { level: heading[1].length, title: heading[2].trim(), from: offset, lines: [] }
    } else {
      current.lines.push(line)
    }
    offset += line.length + 1
  })
  sections.push(current)

  const cast = new Map()
  const analyzed = sections
    .filter((section) => section.title !== null || section.lines.some((line) => line.trim()))
    .map((section) => {
      const body = section.lines.join("\n")
      const entities = new Map()
      for (const match of `${section.title || ""}\n${body}`.matchAll(mentionPattern())) {
        const entity = matchedEntity(match)
        if (!entity) continue
        entities.set(entity.id, entity)
        cast.set(entity.id, (cast.get(entity.id) || 0) + 1)
      }
      return { ...section, entities: [...entities.values()] }
    })

  const headings = analyzed.filter((section) => section.title !== null)
  const documentName = headings.find((section) => section.level === 1)?.title || "Untitled"

  const byLevel = new Map()
  headings.forEach((section) => {
    const skipAsDocumentTitle = section.level === 1 && section.title === documentName && !byLevel.has(1)
    if (skipAsDocumentTitle && headings.filter((h) => h.level === 1).length === 1) return
    if (!byLevel.has(section.level)) byLevel.set(section.level, [])
    byLevel.get(section.level).push(section)
  })

  const castByKind = new Map()
  ;[...cast.entries()].forEach(([id, count]) => {
    const entity = store.findEntity(id)
    if (!entity) return
    if (!castByKind.has(entity.kind)) castByKind.set(entity.kind, [])
    castByKind.get(entity.kind).push({ entity, count })
  })
  castByKind.forEach((rows) => rows.sort((a, b) => b.count - a.count))

  return { documentName, byLevel, castByKind }
}

export function buildInspectorPane({ text, onTravel, onOpenEntity, onOpenFlow, onHighlightChange }) {
  const { documentName, byLevel, castByKind } = analyzeDocument(text)

  const pane = document.createElement("section")
  pane.className = "pane"
  pane.tabIndex = -1
  pane.dataset.workspaceTarget = "pane"
  pane.dataset.inspector = "true"

  const header = document.createElement("header")
  header.className = "px-5 pt-5 pb-3 border-b border-nord3/40"
  const eyebrow = document.createElement("p")
  eyebrow.className = "text-[11px] uppercase tracking-widest text-nord4/50"
  eyebrow.textContent = "Your outline structure for"
  header.appendChild(eyebrow)
  const title = document.createElement("h2")
  title.className = "mt-1 text-xl font-bold text-nord6 truncate"
  title.textContent = documentName
  header.appendChild(title)
  pane.appendChild(header)

  const empty = byLevel.size === 0 && castByKind.size === 0
  if (empty) {
    const message = document.createElement("p")
    message.className = "px-5 py-6 text-sm text-nord4/50 italic"
    message.textContent = "No outline found yet. Start writing it now!"
    pane.appendChild(message)
    return pane
  }

  const levels = [...byLevel.keys()].sort()
  levels.forEach((level) => {
    const headings = byLevel.get(level)
    const group = document.createElement("section")
    group.className = "px-3 py-3"

    const label = document.createElement("h3")
    label.className = "px-2 pb-1 flex items-baseline gap-2"
    const noun = document.createElement("span")
    noun.className = "text-base font-bold text-nord6"
    noun.textContent = groupLabel(headings) || `H${level}`
    label.appendChild(noun)
    const count = document.createElement("span")
    count.className = "text-xs text-nord13"
    count.textContent = `(${headings.length})`
    label.appendChild(count)
    group.appendChild(label)

    const list = document.createElement("ol")
    headings.forEach((section) => {
      const item = document.createElement("li")
      const row = document.createElement("button")
      row.type = "button"
      row.className = "inspector-section w-full text-left"
      row.dataset.entityIds = section.entities.map((entity) => entity.id).join(",")

      const text = document.createElement("span")
      text.className = "block text-sm text-nord4/90 truncate"
      text.textContent = section.title
      row.appendChild(text)

      row.addEventListener("click", () => onTravel(section.from))
      updateSectionTint(row)
      item.appendChild(row)
      list.appendChild(item)
    })
    group.appendChild(list)
    pane.appendChild(group)
  })

  KIND_ORDER.forEach((kind) => {
    const rows = castByKind.get(kind)
    if (!rows?.length) return

    const group = document.createElement("section")
    group.className = "px-3 py-3 border-t border-nord3/40"

    const label = document.createElement("h3")
    label.className = `px-2 pb-1 text-base font-bold inspector-kind-${kind}`
    label.textContent = titleize(pluralize(kind))
    group.appendChild(label)

    rows.forEach(({ entity, count }) => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "inspector-cast w-full"
      row.dataset.entityId = String(entity.id)
      if (store.highlighted.has(entity.id)) row.classList.add("is-highlighted")

      const name = document.createElement("span")
      name.className = "truncate text-sm text-nord4"
      name.textContent = entity.name
      row.appendChild(name)

      const tally = document.createElement("span")
      tally.className = "text-xs text-nord4/50 tabular-nums"
      tally.textContent = `${count}×`
      row.appendChild(tally)

      const flow = document.createElement("span")
      flow.className = "inspector-action"
      flow.textContent = "∿"
      flow.title = "See in Flow"
      flow.addEventListener("click", (event) => {
        event.stopPropagation()
        onOpenFlow(entity)
      })
      row.appendChild(flow)

      const open = document.createElement("span")
      open.className = "inspector-action"
      open.textContent = "→"
      open.title = "Open profile"
      open.addEventListener("click", (event) => {
        event.stopPropagation()
        onOpenEntity(entity)
      })
      row.appendChild(open)

      row.addEventListener("click", () => onHighlightChange(entity.id))
      group.appendChild(row)
    })
    pane.appendChild(group)
  })

  return pane
}

export function refreshInspectorHighlights(pane) {
  pane.querySelectorAll(".inspector-section").forEach(updateSectionTint)
  pane.querySelectorAll(".inspector-cast").forEach((row) => {
    row.classList.toggle("is-highlighted", store.highlighted.has(Number(row.dataset.entityId)))
  })
}

function updateSectionTint(row) {
  const ids = (row.dataset.entityIds || "").split(",").filter(Boolean).map(Number)
  const contains = ids.some((id) => store.highlighted.has(id))
  row.classList.toggle("contains-highlight", contains)
}
